/** 表示用の書式。数値の丸め方は「読んで判断できる桁」に寄せている。 */

import type { Verdict } from './stats/analysis'

/** 判定の見出し。画面もレポートもこの文言を使う。 */
export const VERDICT_LABELS: Record<Verdict, string> = {
  improved: '有意に改善',
  worsened: '有意に悪化',
  inconclusive: '有意差なし',
}

/** 比率を % 表記にする。 */
export function formatPercent(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

/** 比率の差をポイント表記にする。符号を必ず付ける。 */
export function formatPoints(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(digits)} pt`
}

/** 相対リフトを % 表記にする。符号を必ず付ける。 */
export function formatLift(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(digits)}%`
}

/**
 * p 値を表示する。
 * 極端に小さい値を 0.000 と丸めると「差が確実」と誤読されるため、
 * 下限を設けて "< 0.0001" と表示する。
 */
export function formatPValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  if (value < 0.0001) return '< 0.0001'
  if (value >= 0.9995) return '> 0.999'
  return value.toFixed(4)
}

/**
 * 信頼水準を表示する。
 *
 * 逐次確認では有意水準が 1e-5 台まで下がるため、0 桁で丸めると
 * 区間の水準がどれも「100%」になってしまう。100% と見分けが付く桁まで
 * 自動で伸ばし、区間がなぜ広いのかを読めるようにする。
 */
export function formatConfidence(confidence: number): string {
  if (!Number.isFinite(confidence)) return '—'
  if (confidence >= 1) return '100%'
  for (let digits = 0; digits < 4; digits++) {
    const text = (confidence * 100).toFixed(digits)
    if (Number(text) < 100) return `${text}%`
  }
  return `${(confidence * 100).toFixed(4)}%`
}

/**
 * 有意水準を表示する。
 * 逐次確認の初回のように 1e-5 台まで下がる値があるため、
 * p 値の表示と違って丸めずに指数表記へ切り替える。基準の値そのものを見せる欄なので、
 * 「< 0.0001」では何を満たせばよいのか分からない。
 */
export function formatAlpha(value: number): string {
  if (!Number.isFinite(value)) return '—'
  if (value < 0.0001) return value.toExponential(1)
  return value.toFixed(4)
}

/** 区間を表示する。片側検定では無限側を記号にする。 */
export function formatInterval(
  interval: { low: number; high: number } | null,
  format: (value: number) => string,
): string {
  if (!interval) return '—'
  const low = interval.low === -Infinity ? '−∞' : format(interval.low)
  const high = interval.high === Infinity ? '+∞' : format(interval.high)
  return `${low} ～ ${high}`
}

/**
 * 単位の決まっていない測定値を表示する。
 *
 * 客単価 (1,200)、滞在時間 (95.4)、1 人あたり PV (2.35) のように
 * 桁がまるで違う値が同じ欄に来るため、値の大きさで小数点以下を決める。
 * 固定桁にすると、大きい値では読めない桁が並び、小さい値では潰れる。
 */
export function formatMeasure(value: number, signed = false): string {
  if (!Number.isFinite(value)) return '—'
  const magnitude = Math.abs(value)
  const digits = magnitude >= 100 ? 1 : magnitude >= 1 ? 2 : 4
  const text = magnitude.toLocaleString('ja-JP', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  if (!signed) return value < 0 ? `−${text}` : text
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${text}`
}

/** 整数を桁区切りで表示する。 */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('ja-JP')
}

/** 日数を「◯日」または「◯ヶ月」に丸める。 */
export function formatDuration(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return '—'
  if (days < 1) return '1 日未満'
  if (days < 60) return `約 ${Math.ceil(days)} 日`
  const months = days / 30.4
  if (months < 24) return `約 ${months.toFixed(1)} ヶ月`
  return `約 ${(days / 365).toFixed(1)} 年`
}
